import { TowerType, StatusEffect, EnemyType } from './types';
import { Vec2, dist, sub, normalize } from './vec';
import { EnemyState, damageEnemy, rollDodge } from './enemy';
import { selectTarget, enemiesInRadius } from './targeting';
import { applyStatus } from './status';
import type { TowerPoison } from './towerPoison';

export interface TowerSpec {
  range: number;
  fireRate: number; // shots/sec; 0 for continuous aura
  damage: number;
  behavior:
    | 'single'
    | 'pierce'
    | 'aura-slow'
    | 'aoe'
    | 'debuff'
    | 'melee'
    | 'global-freeze'
    /**
     * Lightning Hambi: no aiming. Every 1/fireRate seconds, while any enemy
     * is alive, a cloud sets off from the player's BASE (the path's end) and
     * rolls back up the track against the traffic at `projectileSpeed`,
     * striking each enemy it passes for `damage` until it has hit `pierce`
     * of them or reaches the spawn end, where it fades. `range` is unused.
     */
    | 'path-shot';
  /**
   * Structural HP this tower carries when a level arms tower HP (The Hub's
   * levels: `towerHp` on the LevelDef). Unset = the level's number. The
   * Market's Hambis carry 15 against the level's 10.
   */
  hp?: number;
  aoeRadius?: number;
  pierce?: number;
  slow?: { magnitude: number; duration: number };
  vulnerable?: { magnitude: number; duration: number };
  burn?: { magnitude: number; duration: number };
  /**
   * Bow Hambi's poison arrow (spec Revision 7). Every arrow rolls poison with
   * `chance` at FIRE time; a poison arrow deals NO impact damage and instead
   * poisons the enemy it strikes for `magnitude`/s over `duration` seconds.
   * A chance of 0 (tier 1) takes no draw from the RNG at all — see `rollPoison`.
   */
  poisonArrow?: { chance: number; magnitude: number; duration: number };
  /**
   * Arrows loosed per shot (Bow Hambi tier 3 = 2), each at a DIFFERENT enemy
   * when another is in range, else both at the same one. Each arrow rolls its
   * own poison. Default 1.
   */
  volley?: number;
  /**
   * King Capy's gems: the volley is "UP TO `volley`", not "always `volley`".
   * Each extra gem needs an enemy of its OWN and is simply not fired when
   * there isn't one — where Bow Hambi's second arrow doubles up on its first
   * target instead. That cap is the whole balance of him: three gems at
   * 400/500/600 apiece all landing on one boss would be three times the
   * single-target damage these numbers were chosen for.
   */
  volleyDistinct?: boolean;
  /**
   * Mage Capy: draw this tower's shot as a fading LASER BEAM from tower to
   * target instead of a flying sprite. Render-only — the sim still spawns an
   * ordinary projectile, exactly as the Laser Nacho's beam does on the enemy
   * side (`EnemySpec.towerFire.beam`). Nothing about damage, timing or
   * targeting changes, so no seeded stream moves.
   */
  beamShot?: boolean;
  /**
   * King Capy's last act: when this tower is DESTROYED (The Hub's levels arm
   * tower HP), it fires `count` parting gems, each worth `spec.damage`, at
   * that many DISTINCT enemies drawn at random from everything still alive on
   * the map — his range does not apply, and fewer than `count` alive simply
   * means fewer gems. The picks are the only RNG draws involved and they are
   * taken solely on the tick he dies.
   */
  deathBurst?: { count: number };
  /**
   * King Hambi's yellow shards: every shard rolls `chance` at FIRE time to
   * SPLIT in two, the second half flying at a DIFFERENT enemy in range. With
   * no second enemy in range the split simply has nowhere to go and is not
   * fired (the `volleyDistinct` rule — two halves on one target would double
   * the single-target damage). The draw is taken only for a spec that HAS
   * this field, so every other tower's seeded stream is untouched.
   */
  splitShot?: { chance: number };
  /**
   * King Hambi's K–O (spec 2026-09-22): every `interval` seconds a rain of
   * shards falls over the whole map and deals `damage` to EVERY enemy alive
   * when it lands, `delay` seconds after the cast. A charged rain is HELD
   * while nothing is on the track — it never goes off at an empty map — and
   * the clock restarts only when it actually casts. The render layer pauses
   * the game for the ceremony off the `shardRainCast` event; the sim does not
   * know or care, which is why the landing is a fixed delay after the cast.
   */
  shardRain?: { interval: number; damage: number; delay: number };
  /**
   * The Ham Reaper's scythe (spec 2026-10-12): every `interval` seconds a huge
   * scythe crosses the WHOLE screen from the right edge to the left in
   * `duration` seconds, dealing `damage` once to every enemy the blade passes —
   * i.e. every enemy alive on the field while it crosses, hit as the blade
   * reaches its x. Charged and HELD like `shardRain` (never at an empty
   * track), on its own events (`scytheCast` / `scytheEnded`), so it can never
   * pay the K–O's Golden Stone.
   */
  scytheSweep?: { interval: number; damage: number; duration: number };
  /**
   * The Golden Shroom's SHROOM FRENZY (spec 2026-10-27): the K–O's clock and
   * rules — every `interval` seconds, HELD while the track is empty — landing
   * `damage` on EVERY living enemy `delay` sim seconds after the cast. Its own
   * events (`shroomFrenzyCast` / `shroomFrenzyLanded`): never `shardRainCast`,
   * which pays a Golden Stone. The render layer's 5 s cutscene pauses the sim.
   */
  shroomFrenzy?: { interval: number; damage: number; delay: number };
  /**
   * Healer Hambi (spec 2026-10-13): every `interval` seconds he gives `amount`
   * HP back to every tower whose plot lies within his `range` — himself
   * included — never past its `maxHp`. Tower HP only exists where a level arms
   * it (The Hub and the Origin Event), so everywhere else the pulse finds
   * nothing to heal and does nothing. The clock runs on the tower's own time,
   * so a stunned or possessed Healer stops healing.
   */
  healPulse?: { interval: number; amount: number };
  /**
   * Banking Burger (spec 2026-10-13): every enemy KILLED anywhere on the map
   * — not leaked — pays `cash` on top of its bounty while he stands on the
   * board. His presence, not an action (like the Grease Goddess's grease), so a
   * stunned Banker still pays. He is `unique`, so it is never paid twice.
   */
  killBonus?: { cash: number };
  projectileSpeed?: number; // px/sec; omitted for aura-slow (no projectile)
  /** Melee: unit travel speed out to the target and back (px/sec). */
  moveSpeed?: number;
  /** Melee: radius (px) of the slash — the target plus any enemy within it are hit. */
  cleaveRadius?: number;
  /** Melee: seconds the unit waits at its base after returning, before the next lunge. */
  strikeWait?: number;
  /** Melee: hits landed per strike (each deals `damage`); default 1. The Ninja Hambi lands 3. */
  hits?: number;
  /** When true this tower is immune to the Null-Hex stun (never chosen as a victim). */
  stunImmune?: boolean;
  /** AoE salvo: bombs thrown per volley (default 1). */
  salvo?: number;
  /** AoE salvo: seconds between bombs within a volley. */
  salvoDelay?: number;
  /** global-freeze: seconds between map-wide freezes (the ability cooldown). */
  abilityCooldown?: number;
  /** global-freeze: seconds each freeze lasts. */
  freezeDuration?: number;
  /**
   * Timed special shot: an extra, bigger projectile every `interval` seconds.
   * With `hitscan` the damage is applied at FIRE time instead of spawning a
   * projectile (Phantom Hambi's red bullet — the render layer plays a paused
   * cinematic off the `specialFired` event and the shot cannot whiff).
   */
  special?: {
    interval: number;
    damage: number;
    hitscan?: boolean;
    /**
     * Galactic Capy's black fireball: this shot ALONE splashes the enemy ahead
     * of and the one behind whatever it hits (see `neighborSplash`). Kept here
     * rather than on the spec because his ordinary purple fireballs must not
     * splash — a spec-wide flag would have given every shot the black one's
     * reach.
     */
    neighborSplash?: boolean;
  };
  /**
   * Per-shot damage roll (Gunner Hambi, Fire Mage). Every shot lands in exactly
   * one band: a headshot, a critical, or — for the remaining probability — an
   * ordinary shot dealing plain `damage`. The normal case is deliberately NOT a
   * third entry here, so the chances cannot drift out of sum.
   *
   * `headshot` is optional: a tower with only two outcomes (the Fire Mage's 25%
   * blue fireball) omits it rather than declaring a 0%-chance band, which would
   * otherwise print as "0% headshot" everywhere the spec is described.
   *
   * Unset on every other tower, which is what keeps them from drawing on the
   * sim's RNG at all (see `updateTower`).
   */
  shotRoll?: {
    crit?: { chance: number; damage: number };
    headshot?: { chance: number; damage: number };
  };
  /**
   * The Aqua Sorcerer's droplet: on impact it also strikes the nearest living
   * enemy AHEAD of the one it hit and the nearest one BEHIND it along the path,
   * each for the same damage. Neighbours are picked by path progress, not by
   * proximity to the impact point, and there is no distance cap — the droplet
   * always catches three when three are on the track.
   */
  neighborSplash?: boolean;
  /**
   * General Capy's red imposter (spec 2026-09-16). Every runner he sends up
   * the track rolls `chance` at FIRE time; a red runner deals `damage` instead
   * of `spec.damage` and is drawn in the minigame's red frames. A chance of 0
   * — or no `spawnRoll` at all — takes no draw from the RNG whatsoever, which
   * is what leaves Lightning Hambi's seeded stream untouched (see `rollSpawn`).
   */
  spawnRoll?: { chance: number; damage: number };
  /**
   * Leafstorm Hambi's tornado (spec 2026-09-26): it flies at its target like
   * any single shot, and where it LANDS the leaves spin out into the
   * `targets` living enemies nearest that point — his target among them —
   * each taking full `damage`. Only enemies within the tower's own `range` of
   * his plot can be caught ("the 4 closest in range"), so a tornado never
   * reaches across the map. No RNG is involved.
   */
  tornado?: { targets: number };
  /**
   * Samurai Slicer's thrown daggers — what makes him a MULTI-ATTACKER (spec
   * 2026-09-26). He is melee (`range` is his INNER ring, the slash), and
   * `delay` seconds after each slash lands he rolls `chance` ONCE; on success
   * he throws `count` small daggers from his plot, each at a DIFFERENT enemy
   * inside `range` here — his OUTER ring — for `damage` apiece. Fewer enemies
   * than `count` means fewer daggers (the `volleyDistinct` rule), never two on
   * one target. The roll is the only RNG draw and is taken only by a spec that
   * has this field, so every other seeded stream is untouched.
   */
  midStrike?: { delay: number; chance: number; count: number; damage: number; range: number };
  /**
   * The Grease Goddess (spec 2026-09-29): every OTHER tower whose plot lies
   * within this tower's `range` deals `damage` more (0.25 = +25%), added to
   * its `damageMult` exactly as potions and stones add to each other. It does
   * not stack — a tower under two Goddesses gets it once — and it is her
   * presence, not an action, so a stunned Goddess still greases.
   */
  allyBoost?: { damage: number };
  /**
   * Ultra Hammy: this tower simply HAS four tiers — its `tier4` is an ordinary
   * upgrade, not an Evolution, so `maxTierFor` allows it in every run. Read
   * off tier 1.
   */
  nativeTier4?: boolean;
  /**
   * Ultra Hammy: only one of this tower type may stand on the board at once.
   * Selling it or losing it to a fireball frees the slot.
   */
  unique?: boolean;
  /**
   * Ultra Hammy's END THEM (spec 2026-09-29, tier 4). Charges like King
   * Hambi's K–O — `interval` seconds on the tower's own clock, HELD while the
   * track is empty — then: every tower deals `boost` more for `duration`
   * seconds, `damage` lands on every enemy alive `delay` seconds after the
   * cast, and `minions.count` tier-2 minions throw `minions.damage` at any
   * enemy on the map every `minions.interval` seconds for `duration` seconds.
   * Its own events (`endThemCast` / `endThemLanded`), never the K–O's: an END
   * THEM must not bank a Golden Stone.
   */
  endThem?: {
    interval: number;
    damage: number;
    delay: number;
    boost: number;
    duration: number;
    minions: { count: number; interval: number; damage: number; speed: number };
  };
  cost: number;
  upgradeCost: number;
}

/** Which band a `shotRoll` shot landed in. */
export type ShotKind = 'normal' | 'crit' | 'headshot';

/** Anything that can hand out a 0..1 number — the sim's seeded Rng, or a test stub. */
export interface RandomSource {
  next(): number;
}

/**
 * Pick a shot's band from one random draw. Band order is fixed — headshot
 * first, then crit, then the normal remainder — so a test can force a branch by
 * injecting a known number, and so the mapping never silently reshuffles.
 */
export function rollShot(spec: TowerSpec, rng: RandomSource): ShotKind {
  if (!spec.shotRoll) return 'normal';
  const head = spec.shotRoll.headshot?.chance ?? 0;
  const crit = spec.shotRoll.crit?.chance ?? 0;
  const r = rng.next();
  if (head > 0 && r < head) return 'headshot';
  if (crit > 0 && r < head + crit) return 'crit';
  return 'normal';
}

/** What a shot of the given band is worth for this spec. */
export function shotDamage(spec: TowerSpec, kind: ShotKind): number {
  if (!spec.shotRoll || kind === 'normal') return spec.damage;
  // A band that this spec does not declare cannot be rolled; falling back
  // through the other band and then to plain damage keeps the function total
  // rather than returning undefined. Mini Gunner declares only `headshot`
  // (his 20% is literally a HEADSHOT, text and all) and King Capy only `crit`
  // (his blue gem), so each spec is missing one of the two.
  if (kind === 'headshot') {
    return spec.shotRoll.headshot?.damage ?? spec.shotRoll.crit?.damage ?? spec.damage;
  }
  return spec.shotRoll.crit?.damage ?? spec.damage;
}

/** A bomb queued to launch later in the same salvo volley. */
export interface PendingShot {
  /** Seconds remaining until this bomb launches. */
  delay: number;
  /** Enemy the volley locked onto (re-aimed at it if still alive/in range). */
  targetId: number;
  /** Where bomb #1 was aimed; used if the locked target is gone. */
  fallbackTo: Vec2;
}

export interface MeleeState {
  phase: 'idle' | 'out' | 'back';
  /** Current unit position; equals the tower's base plot pos when idle. */
  pos: Vec2;
  /** Enemy id currently being pursued (null when idle/returning). */
  targetId: number | null;
  /** Remaining post-strike wait (seconds); counts down during the idle phase. */
  wait: number;
}

export interface TowerState {
  id: number;
  type: TowerType;
  tier: 1 | 2 | 3 | 4;
  plotId: number;
  pos: Vec2;
  cooldown: number;
  /** Present only for global-freeze towers; seconds until the next map-wide freeze. */
  abilityCooldown?: number;
  /** Present only for melee towers; drives the dash-out / strike / dash-back cycle. */
  melee?: MeleeState;
  /**
   * Second melee copy — the tier-4 Ninja Hambi's Evolution splits it into two
   * units that flurry two enemies at once. Created when the tower reaches
   * tier 4; absent everywhere else.
   */
  melee2?: MeleeState;
  /** Present only for salvo (multi-bomb) AoE towers; bombs awaiting release. */
  salvoQueue?: PendingShot[];
  /**
   * Present only for `midStrike` towers (Samurai Slicer), and only between a
   * slash landing and his dagger roll: seconds until he rolls. Undefined the
   * rest of the time, so a slash is what arms it.
   */
  midStrikeIn?: number;
  /** Present only for `shardRain` towers (King Hambi); seconds until the next K–O. */
  rainCooldown?: number;
  /** Present only for `scytheSweep` towers (The Ham Reaper); seconds until the next sweep. */
  scytheCooldown?: number;
  /** Present only for `shroomFrenzy` towers (The Golden Shroom); seconds until the next. */
  frenzyCooldown?: number;
  /**
   * Hypnotised by a Hyno-Fungi into a SHORTER range: seconds left, and the
   * multiplier the tower loop folds into the spec every behaviour reads.
   */
  rangeShrinkFor?: number;
  rangeShrinkMult?: number;
  /** Present only for `healPulse` towers (Healer Hambi); seconds until the next heal. */
  healCooldown?: number;
  /** Present only for `endThem` towers (Ultra Hammy at tier 4); seconds until END THEM. */
  endThemCooldown?: number;
  /** Present only for towers with a timed special shot; seconds until charged. */
  specialCooldown?: number;
  /**
   * Seconds of Null-Hex stun remaining. While > 0 the tower is skipped
   * entirely each tick (no shots, aura, melee, or ability-cooldown progress).
   */
  stunnedFor?: number;
  /**
   * Seconds of Veggie-Hex fire-rate slow remaining (the Brocco's hex). While
   * > 0 this tower's firing clock advances at (1 - fireSlowFactor) speed.
   */
  fireSlowFor?: number;
  /** Fraction (0..1) by which the Veggie Hex slows this tower's fire rate. */
  fireSlowFactor?: number;
  /**
   * Structural HP, present ONLY once an Ember Barrage carrier (King Ember) is
   * on the field — undefined for the whole rest of the campaign, which is what
   * hides the HP bar everywhere else. Each fireball hit costs 1; at 0 the tower
   * is removed and its plot frees up.
   */
  hp?: number;
  /** Full structural HP (TOWER_MAX_HP) — set alongside `hp`, undefined with it. */
  maxHp?: number;
  /**
   * The Shroom's dodge stream (mulberry32), seeded from the tower's id the
   * first time it rolls — see `rollTowerDodge`.
   */
  dodgeSeed?: number;
  /** The Snowshroom: seconds until this tower can shroomnitize again (0 = ready). */
  shroomnitizeIn?: number;
  /**
   * Possessed by a ghost (see EnemySpec.ghosting): the tower attacks no enemy
   * while this is set. `nextIn` seconds until it strikes another tower;
   * `hitsLeft` strikes to go, each at a tower not in `struck`. Cleared when
   * the last strike has gone. `by` is the possessor, for the render's tint.
   */
  possessed?: {
    by: EnemyType;
    hitsLeft: number;
    damage: number;
    nextIn: number;
    gap: number;
    struck: number[];
    /** Atlas key its strikes fly as; unset = the ghost's `proj-ghostwisp`. */
    projectile?: string;
  };
  /**
   * Poisoned by a Poison Shroom's dose (EnemySpec.towerFire.poison): HP comes
   * off on its own clock until the dose runs out. The tower keeps attacking.
   */
  poison?: TowerPoison;
}

export interface ProjectileSpawn {
  from: Vec2;
  to: Vec2;
  /** Path-shot only: this runner is General Capy's red imposter. */
  redSpawn?: boolean;
  spec: TowerSpec;
  towerType: TowerType;
  tier: 1 | 2 | 3 | 4;
  speed: number;
  /** True for a timed special shot (e.g. the wizard's big green bolt). */
  special?: boolean;
  /** Hitscan special: the sim applies the damage directly — no projectile. */
  hitscan?: boolean;
  /** Hitscan special only: the enemy the shot locked at fire time. */
  targetId?: number;
  /** `shotRoll` towers only: which band this bullet rolled when it was fired. */
  shotKind?: ShotKind;
  /** `shotRoll` towers only: the rolled damage, spent instead of `spec.damage`. */
  damageOverride?: number;
  /** Path-shot only: the track distance the cloud starts at (the path's length). */
  pathDist?: number;
  /**
   * Path-shot only: which of the level's routes this cloud rolls up, indexing
   * `GameSim.routes`. 0 on every single-entrance level.
   */
  route?: number;
  /** Bow Hambi: this arrow flies poisoned (no impact damage; poisons on contact). */
  poison?: boolean;
}

/**
 * Bow Hambi: does this arrow fly poisoned? Rolled at fire time, one draw per
 * arrow. A spec with no poison band, or a 0% one (tier 1), draws NOTHING, so
 * the RNG stream of every other tower stays untouched.
 */
export function rollPoison(spec: TowerSpec, rng?: RandomSource): boolean {
  if (!spec.poisonArrow || spec.poisonArrow.chance <= 0 || !rng) return false;
  return rng.next() < spec.poisonArrow.chance;
}

/**
 * Does THIS runner come out red? Same contract as `rollPoison`: no spec, or a
 * chance of 0, means no draw at all, so a `path-shot` tower without a
 * `spawnRoll` (Lightning Hambi) leaves every seeded stream exactly as it was.
 */
export function rollSpawn(spec: TowerSpec, rng?: RandomSource): boolean {
  const chance = spec.spawnRoll?.chance ?? 0;
  if (chance <= 0 || !rng) return false;
  return rng.next() < chance;
}

/**
 * Lightning Hambi's clock. He never aims: once the cooldown is spent and
 * anything is alive on the track, a cloud sets off from the player's BASE
 * (`pathEnd`, at `pathLength` along the track) towards the spawn end
 * (`pathStart`). The tower's own position plays no part — the plot is only
 * where he stands. Returns the spawn, or null when nothing fires this tick.
 */
/**
 * One route as a rolling cloud sees it: how far it has to travel, where it
 * leaves from, and the mouth it vanishes at. The sim reduces its `Path`s to
 * these once, so `tower.ts` needs no `Path` of its own.
 */
export interface PathShotRoute {
  /** Total track length of the route, lead-in included. */
  length: number;
  /** The exit end, where the cloud is born. */
  end: Vec2;
  /** The entrance mouth, where the cloud dies. */
  start: Vec2;
}

export function updatePathShotTower(
  t: TowerState,
  dt: number,
  spec: TowerSpec,
  enemies: EnemyState[],
  routes: readonly PathShotRoute[],
  rng?: RandomSource,
): ProjectileSpawn | null {
  t.cooldown = Math.max(0, t.cooldown - dt);
  if (t.cooldown > 0) return null;
  if (!enemies.some((e) => e.alive)) return null;
  t.cooldown = spec.fireRate > 0 ? 1 / spec.fireRate : 0;
  // Where several entrances merge, the trunk alone stops the cloud dead at the
  // junction — so a cloud rolls all the way up ONE lead-in, drawn fresh every
  // shot, and vanishes at that mouth. The draw is guarded on `length > 1` so a
  // single-entrance level takes nothing off the run's Rng and every seeded
  // stream in the game stays exactly as it was.
  const i = routes.length > 1 && rng ? Math.floor(rng.next() * routes.length) % routes.length : 0;
  const route = routes[i];
  // General Capy's imposter: rolled here, at fire time, so the runner that sets
  // off already knows what it is worth and the render layer can pick its frames.
  const red = rollSpawn(spec, rng);
  return {
    from: { x: route.end.x, y: route.end.y },
    to: { x: route.start.x, y: route.start.y },
    spec,
    towerType: t.type,
    tier: t.tier,
    speed: spec.projectileSpeed ?? 220,
    pathDist: route.length,
    route: i,
    ...(red ? { redSpawn: true, damageOverride: spec.spawnRoll?.damage } : {}),
  };
}

/**
 * Lightning Hambi against an ANCHORED boss.
 *
 * His cloud normally rolls up the track from the base — that is the whole point
 * of `path-shot` — but Prince Nacho's second form floats 120px clear of the
 * nearest lane, four times the cloud's 28px hit radius, so a roll can never
 * touch him. The brief asks that every tower attack the second form, so against
 * an off-track target he throws the bolt straight from his plot instead. Same
 * cooldown, same damage, same art: only the delivery changes.
 */
export function updateBoltShotTower(
  t: TowerState,
  dt: number,
  spec: TowerSpec,
  target: EnemyState,
): ProjectileSpawn | null {
  t.cooldown = Math.max(0, t.cooldown - dt);
  if (t.cooldown > 0) return null;
  t.cooldown = spec.fireRate > 0 ? 1 / spec.fireRate : 0;
  return {
    from: { x: t.pos.x, y: t.pos.y },
    // An anchored enemy does not move, so there is nothing to lead.
    to: { x: target.pos.x, y: target.pos.y },
    spec,
    towerType: t.type,
    tier: t.tier,
    speed: spec.projectileSpeed ?? 220,
  };
}

export function updateTower(
  t: TowerState,
  dt: number,
  spec: TowerSpec,
  enemies: EnemyState[],
  rng?: RandomSource,
): ProjectileSpawn[] {
  if (spec.behavior === 'aura-slow') {
    if (spec.slow) {
      for (const e of enemies) {
        if (!e.alive || e.slowImmune) continue;
        if (Math.hypot(e.pos.x - t.pos.x, e.pos.y - t.pos.y) <= spec.range) {
          const s: StatusEffect = {
            kind: 'slow',
            magnitude: spec.slow.magnitude,
            remaining: spec.slow.duration,
          };
          applyStatus(e.statuses, s);
        }
      }
    }
    return [];
  }
  t.cooldown = Math.max(0, t.cooldown - dt);

  const spawns: ProjectileSpawn[] = [];

  // Release any queued salvo bombs whose delay has elapsed. Re-aim at the locked
  // target if it is still alive and in range; otherwise reuse bomb #1's landing
  // point so the extra bomb still lands on roughly the same spot.
  if (t.salvoQueue && t.salvoQueue.length > 0) {
    const speed = spec.projectileSpeed ?? 600;
    const remaining: PendingShot[] = [];
    for (const shot of t.salvoQueue) {
      shot.delay -= dt;
      if (shot.delay > 0) {
        remaining.push(shot);
        continue;
      }
      const target = enemies.find((e) => e.id === shot.targetId && e.alive);
      let to = shot.fallbackTo;
      if (target && dist(t.pos, target.pos) <= spec.range) {
        const tHit = dist(target.pos, t.pos) / speed;
        to = { x: target.pos.x + target.vel.x * tHit, y: target.pos.y + target.vel.y * tHit };
      }
      spawns.push({
        from: { x: t.pos.x, y: t.pos.y },
        to,
        spec,
        towerType: t.type,
        tier: t.tier,
        speed,
      });
    }
    t.salvoQueue = remaining;
  }

  // Timed special shot (the wizard's green bolt): charges on its own clock,
  // independent of the normal cooldown. A finished charge is HELD until a
  // target is in range; the clock only restarts after the shot actually fires.
  if (spec.special) {
    if (t.specialCooldown === undefined) t.specialCooldown = spec.special.interval;
    t.specialCooldown = Math.max(0, t.specialCooldown - dt);
    if (t.specialCooldown <= 0) {
      const target = selectTarget(t.pos, spec.range, enemies);
      if (target) {
        t.specialCooldown = spec.special.interval;
        const speed = spec.projectileSpeed ?? 600;
        const tHit = Math.hypot(target.pos.x - t.pos.x, target.pos.y - t.pos.y) / speed;
        spawns.push({
          from: { x: t.pos.x, y: t.pos.y },
          // Hitscan lands where the target IS; a projectile leads its flight.
          to: spec.special.hitscan
            ? { x: target.pos.x, y: target.pos.y }
            : { x: target.pos.x + target.vel.x * tHit, y: target.pos.y + target.vel.y * tHit },
          spec,
          towerType: t.type,
          tier: t.tier,
          speed,
          special: true,
          hitscan: spec.special.hitscan,
          targetId: spec.special.hitscan ? target.id : undefined,
        });
      }
    }
  }

  if (t.cooldown > 0) return spawns;
  const target = selectTarget(t.pos, spec.range, enemies);
  if (!target) return spawns;
  t.cooldown = spec.fireRate > 0 ? 1 / spec.fireRate : 0;
  const speed = spec.projectileSpeed ?? 600;
  // Lead the target: aim where it will be after the projectile's flight time,
  // using the enemy's current velocity. Cuts the "always lagging" miss,
  // especially for slow projectiles like the sauce cannon.
  const leadTo = (e: EnemyState): Vec2 => {
    const tHit = Math.hypot(e.pos.x - t.pos.x, e.pos.y - t.pos.y) / speed;
    return { x: e.pos.x + e.vel.x * tHit, y: e.pos.y + e.vel.y * tHit };
  };
  const to = leadTo(target);
  // Roll this shot's band at FIRE time, so the number that lands is the number
  // that was rolled when the trigger was pulled — a bullet in flight cannot
  // change its mind. The draw is taken ONLY for a `shotRoll` tower, which is
  // what leaves every other level's RNG stream (the Null-Hex and Veggie-Hex
  // victim picks) bit-for-bit unchanged. Bow Hambi's poison roll follows the
  // same rule (`rollPoison`: no band, no draw).
  const kind = spec.shotRoll && rng ? rollShot(spec, rng) : undefined;
  spawns.push({
    from: { x: t.pos.x, y: t.pos.y },
    to,
    spec,
    towerType: t.type,
    tier: t.tier,
    speed,
    ...(kind ? { shotKind: kind, damageOverride: shotDamage(spec, kind) } : {}),
    ...(rollPoison(spec, rng) ? { poison: true } : {}),
  });

  // King Hambi's split: one draw per shard, taken only for a `splitShot`
  // spec. The second half needs an enemy of its own (see `splitShot`).
  const chosen = [target.id];
  if (spec.splitShot && rng && rng.next() < spec.splitShot.chance) {
    const other = selectTarget(
      t.pos,
      spec.range,
      enemies.filter((e) => e.id !== target.id),
    );
    if (other) {
      chosen.push(other.id);
      spawns.push({
        from: { x: t.pos.x, y: t.pos.y },
        to: leadTo(other),
        spec,
        towerType: t.type,
        tier: t.tier,
        speed,
      });
    }
  }

  // Bow Hambi's volley (tier 3): the extra arrows each pick a DIFFERENT enemy
  // in range when there is one, and fall back to the first target otherwise.
  // Every arrow rolls its own poison.
  //
  // King Capy's gems are the same loop with `volleyDistinct`: an extra gem
  // that finds no enemy of its own is NOT fired, where Bow Hambi's extra arrow
  // falls back to the first target. Each extra shot also rolls its own band,
  // which is what makes every one of his three gems independently blue — the
  // draw is taken only when the spec HAS a `shotRoll`, so Bow Hambi (which has
  // none) takes no new draw and its seeded stream is bit-for-bit as it was.
  const volley = spec.volley ?? 1;
  for (let i = 1; i < volley; i++) {
    const found = selectTarget(
      t.pos,
      spec.range,
      enemies.filter((e) => !chosen.includes(e.id)),
    );
    if (!found && spec.volleyDistinct) continue;
    const other = found ?? target;
    chosen.push(other.id);
    const extraKind = spec.shotRoll && rng ? rollShot(spec, rng) : undefined;
    spawns.push({
      from: { x: t.pos.x, y: t.pos.y },
      to: leadTo(other),
      spec,
      towerType: t.type,
      tier: t.tier,
      speed,
      ...(extraKind ? { shotKind: extraKind, damageOverride: shotDamage(spec, extraKind) } : {}),
      ...(rollPoison(spec, rng) ? { poison: true } : {}),
    });
  }

  // Queue the rest of a multi-bomb volley at the same target, staggered by salvoDelay.
  const salvo = spec.salvo ?? 1;
  if (salvo > 1) {
    const delay = spec.salvoDelay ?? 0.5;
    if (!t.salvoQueue) t.salvoQueue = [];
    for (let i = 1; i < salvo; i++) {
      t.salvoQueue.push({ delay: delay * i, targetId: target.id, fallbackTo: to });
    }
  }

  return spawns;
}

/**
 * Advance one melee tower's dash cycle. Mutates `t.melee` (movement/phase) and,
 * on the strike tick, applies cleave damage to enemies. Returns true only on the
 * tick a strike lands (so the caller can emit the attack event / SFX).
 *
 * Cycle: idle (scan for target, honour post-strike wait) → out (chase the live
 * target) → STRIKE (cleave the target + neighbours) → back (return to base) →
 * idle (wait = strikeWait). Aborts to `back` if the target dies or leaves range.
 */
export function updateMeleeTower(
  t: TowerState,
  dt: number,
  spec: TowerSpec,
  enemies: EnemyState[],
): boolean {
  const m = t.melee;
  if (!m) return false; // defensive: only melee towers have melee state
  const s1 = updateMeleeUnit(m, t.pos, dt, spec, enemies, null);
  // The tier-4 Ninja's second copy prefers a DIFFERENT enemy than the first
  // (that's the split), but falls back to sharing when only one is in range.
  const s2 = t.melee2 ? updateMeleeUnit(t.melee2, t.pos, dt, spec, enemies, m.targetId) : false;
  return s1 || s2;
}

/**
 * Advance one melee UNIT (a tower has one, the tier-4 Ninja has two). Same
 * contract as updateMeleeTower; `avoidTargetId` steers this unit away from a
 * sibling's current target when any other enemy is available.
 */
function updateMeleeUnit(
  m: MeleeState,
  base: Vec2,
  dt: number,
  spec: TowerSpec,
  enemies: EnemyState[],
  avoidTargetId: number | null,
): boolean {
  const step = (spec.moveSpeed ?? 200) * dt;
  const cleaveR = spec.cleaveRadius ?? 30;

  if (m.phase === 'idle') {
    if (m.wait > 0) {
      m.wait = Math.max(0, m.wait - dt);
      return false;
    }
    let target = selectTarget(base, spec.range, enemies);
    if (target && avoidTargetId != null && target.id === avoidTargetId) {
      const other = selectTarget(
        base,
        spec.range,
        enemies.filter((e) => e.id !== avoidTargetId),
      );
      if (other) target = other;
    }
    if (!target) return false;
    m.targetId = target.id;
    m.phase = 'out';
    return false;
  }

  if (m.phase === 'out') {
    const target = enemies.find((e) => e.id === m.targetId && e.alive);
    // `ignoreRange` (Prince Nacho's second form) is honoured HERE as well as in
    // selectTarget: without it the unit picks him, finds him out of range on the
    // very next tick and turns straight back, so a melee tower would loop
    // out-and-back forever and never land a blow.
    if (!target || (!target.ignoreRange && dist(base, target.pos) > spec.range)) {
      m.targetId = null;
      m.phase = 'back';
      return false;
    }
    const d = dist(m.pos, target.pos);
    if (d <= cleaveR || d <= step) {
      m.pos = { x: target.pos.x, y: target.pos.y };
      // A strike lands `hits` blows (default 1); the Ninja Hambi's flurry lands 3,
      // each dealing full `damage` to every enemy caught in the cleave.
      const hits = spec.hits ?? 1;
      for (const e of enemiesInRadius(m.pos, cleaveR, enemies))
        for (let h = 0; h < hits; h++) if (!rollDodge(e)) damageEnemy(e, spec.damage);
      m.targetId = null;
      m.phase = 'back';
      return true;
    }
    const dir = normalize(sub(target.pos, m.pos));
    m.pos = { x: m.pos.x + dir.x * step, y: m.pos.y + dir.y * step };
    return false;
  }

  // phase === 'back'
  const d = dist(m.pos, base);
  if (d <= step) {
    m.pos = { x: base.x, y: base.y };
    m.phase = 'idle';
    m.wait = spec.strikeWait ?? 1;
    return false;
  }
  const dir = normalize(sub(base, m.pos));
  m.pos = { x: m.pos.x + dir.x * step, y: m.pos.y + dir.y * step };
  return false;
}

/**
 * Samurai Slicer's dagger clock (`TowerSpec.midStrike`). The caller arms
 * `t.midStrikeIn` when a slash lands; this counts it down and, when it runs
 * out, rolls the chance ONCE and returns the daggers to throw — up to `count`
 * of them, each at a different enemy inside the OUTER ring, led like any other
 * shot. Returns nothing on every other tick, on a failed roll, or with nobody
 * in the outer ring (the roll is still spent: the moment has passed).
 */
export function updateMidStrike(
  t: TowerState,
  dt: number,
  spec: TowerSpec,
  enemies: EnemyState[],
  rng?: RandomSource,
): ProjectileSpawn[] {
  const ms = spec.midStrike;
  if (!ms || t.midStrikeIn === undefined) return [];
  t.midStrikeIn -= dt;
  if (t.midStrikeIn > 0) return [];
  t.midStrikeIn = undefined;
  if (!rng || rng.next() >= ms.chance) return [];
  const speed = spec.projectileSpeed ?? 600;
  const spawns: ProjectileSpawn[] = [];
  const chosen: number[] = [];
  for (let i = 0; i < ms.count; i++) {
    const e = selectTarget(
      t.pos,
      ms.range,
      enemies.filter((x) => !chosen.includes(x.id)),
    );
    if (!e) break;
    chosen.push(e.id);
    const tHit = dist(e.pos, t.pos) / speed;
    spawns.push({
      from: { x: t.pos.x, y: t.pos.y },
      to: { x: e.pos.x + e.vel.x * tHit, y: e.pos.y + e.vel.y * tHit },
      spec,
      towerType: t.type,
      tier: t.tier,
      speed,
      damageOverride: ms.damage,
    });
  }
  return spawns;
}

/**
 * Advance one global-freeze tower's ability cooldown. Returns true on the tick
 * the cooldown reaches zero (the caller then starts a map-wide freeze) and
 * resets the cooldown to a full interval. The caller must NOT invoke this while
 * the map is already frozen — the cooldown is meant to pause during the freeze.
 */
export function updateFreezeTower(t: TowerState, dt: number, spec: TowerSpec): boolean {
  if (t.abilityCooldown === undefined) t.abilityCooldown = spec.abilityCooldown ?? 0;
  t.abilityCooldown = Math.max(0, t.abilityCooldown - dt);
  if (t.abilityCooldown <= 0) {
    t.abilityCooldown = spec.abilityCooldown ?? 0;
    return true;
  }
  return false;
}

/**
 * Does this thrown attack miss the tower (the Shroom, spec 2026-10-24)? Rolled
 * on the tower's OWN stream, seeded from its id, never the sim's shared Rng —
 * so a run without the Shroom is bit-for-bit what it was, as with an enemy's
 * `rollDodge`.
 */
export function rollTowerDodge(t: TowerState, chance: number): boolean {
  if (chance <= 0) return false;
  t.dodgeSeed = ((t.dodgeSeed ?? Math.imul(t.id + 1, 0x85ebca6b) >>> 0) + 0x6d2b79f5) >>> 0;
  let x = t.dodgeSeed;
  x = Math.imul(x ^ (x >>> 15), x | 1);
  x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
  return ((x ^ (x >>> 14)) >>> 0) / 4294967296 < chance;
}
